import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
    Avatar
} from '@mui/material';

import {
    Home as HomeIcon,
    Security as SecurityIcon,
    LocalFireDepartment as FireIcon,
    Phone as PhoneIcon,
    Group as GroupIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    EmojiObjects as EmojiObjectsIcon,
    Close as CloseIcon,
    AssignmentInd as AssignmentIndIcon,
    Lock as LockIcon,
    DirectionsRun as DirectionsRunIcon,
    Info as InfoIcon
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', label: 'はじめに' },
    { id: 'joint-disaster-prevention', label: '共同防災対策の基本' },
    { id: 'security-goods', label: '防犯グッズの活用術' },
    { id: 'emergency-contacts', label: '緊急連絡体制の整備' },
    { id: 'roles-in-disaster', label: '災害時の役割分担' },
    { id: 'expert-tips', label: '専門家からのアドバイス' },
    { id: 'summary', label: 'まとめ' }
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 1, sm: 3 }, pb: 8 }}>
            {/* 目次 */}
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)} sx={{ minWidth: 40 }}>
                                <CloseIcon fontSize="small" />
                            </Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{
                                        minWidth: 32,
                                        minHeight: 32,
                                        bgcolor: '#f5f5f5',
                                        color: '#616161',
                                        fontWeight: 'bold',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 1,
                                        fontSize: '1.05rem'
                                    }}>
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{
                                            cursor: 'pointer',
                                            fontSize: '0.96rem',
                                            textAlign: 'left'
                                        }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    ルームシェアの「防災・防犯」対策まとめ
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                    近年、ルームシェアという住まい方が広がる中で、「防災」と「防犯」の重要性はますます高まっています。複数人で暮らすからこそ、<b>共同での備えや連携</b>が大切です。この記事では、ルームシェア生活で絶対に抑えておきたい防災・防犯対策を、実践的なポイントとともにわかりやすく解説します。
                </Typography>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <img
                        src="/images/blog/living-dining_roomshare-bousai-bouhan.jpg"
                        alt="明るい共有リビングでルームメイトたちが食事を楽しみながら、防災について話し合っている様子。白とウッド調のモダンな空間で、安心感と親しみやすさが伝わる雰囲気"
                        style={{
                            borderRadius: 12,
                            maxWidth: '100%',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.09)'
                        }}
                    />
                </Box>
                <Typography variant="body2" color="text.secondary">
                    この記事で紹介する対策を取り入れれば、安心・快適なルームシェア生活を実現することができます。日々の暮らしと、いざという時の備えを両立させましょう。
                </Typography>
            </Box>

            {/* 共同防災対策の基本 */}
            <Box id="joint-disaster-prevention" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    共同防災対策の基本
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    災害時は、パニックや誤解が思わぬトラブルを生むことも。<b>ルームシェアでは「共通ルール」と「備えの見える化」</b>が重要です。
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <FireIcon color="error" sx={{ mr: 1 }} />
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>防災グッズの共有</Typography>
                            </Box>
                            <List>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="非常食・飲料水を家全体で一定量備蓄" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="懐中電灯・ラジオ・モバイルバッテリーの共用" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="消火器や救急箱の設置場所を明確に" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <GroupIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>ルールの策定</Typography>
                            </Box>
                            <List>
                                <ListItem>
                                    <ListItemIcon><AssignmentIndIcon color="action" /></ListItemIcon>
                                    <ListItemText primary="災害時の避難経路や集合場所を全員で確認" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><AssignmentIndIcon color="action" /></ListItemIcon>
                                    <ListItemText primary="防災訓練を年1回以上実施" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><AssignmentIndIcon color="action" /></ListItemIcon>
                                    <ListItemText primary="重要事項は紙やLINEグループで共有" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Box sx={{ my: 2, textAlign: 'center' }}>
                    <img
                        src="/images/blog/emergency-goods_roomshare-bousai-bouhan.jpg"
                        alt="キッチンカウンターに並べられた非常食、水、懐中電灯、救急箱などの防災グッズ。明るい照明の下で、住人たちが一緒に備蓄品のチェックをしている様子"
                        style={{
                            borderRadius: 12,
                            maxWidth: '100%',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.09)'
                        }}
                    />
                </Box>
                <Typography variant="body2" color="text.secondary">
                    防災グッズは使いやすい場所にまとめておき、誰が見ても一目で分かる工夫がポイントです。
                </Typography>
            </Box>

            {/* 防犯グッズの活用術 */}
            <Box id="security-goods" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    防犯グッズの活用術
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアでは「玄関」「共用スペース」「個室」それぞれの防犯を考える必要があります。<b>シンプルで実用的な防犯グッズ</b>の導入で、誰もが安心できる環境を作りましょう。
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={4}>
                        <Card sx={{ maxWidth: 345, height: '100%' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <LockIcon color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>玄関ドアの強化</Typography>
                                </Box>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                        <ListItemText primary="補助錠・ドアストッパーで二重ロック" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                        <ListItemText primary="ドアスコープカバーでのぞき見防止" />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card sx={{ maxWidth: 345, height: '100%' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <SecurityIcon color="action" sx={{ mr: 1 }} />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>共用部の見守り</Typography>
                                </Box>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                        <ListItemText primary="センサーライトの設置" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                        <ListItemText primary="簡易カメラやスマートベル" />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card sx={{ maxWidth: 345, height: '100%' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <HomeIcon color="secondary" sx={{ mr: 1 }} />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>個室も安心</Typography>
                                </Box>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                        <ListItemText primary="個室用の内鍵を設置" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                        <ListItemText primary="貴重品はポータブル金庫へ" />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <Box sx={{ my: 2, textAlign: 'center' }}>
                    <img
                        src="/images/blog/security-device_roomshare-bousai-bouhan.jpg"
                        alt="玄関に補助錠やセンサーライトが設置されており、ルームメイトが説明書を見ながら防犯対策をチェックしている。落ち着いた明るさの空間で安全意識が伝わるシーン"
                        style={{
                            borderRadius: 12,
                            maxWidth: '100%',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.09)'
                        }}
                    />
                </Box>
                <Typography variant="body2" color="text.secondary">
                    どんなグッズも「2人で使い方を共有」して初めて機能します。設置後の説明タイムも忘れずに。
                </Typography>
            </Box>

            {/* 緊急連絡体制の整備 */}
            <Box id="emergency-contacts" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    緊急連絡体制の整備
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    いざという時、<b>「誰にどう連絡すればいいか」</b>が即座に分かる体制を作ることが大切です。スマホやSNSが使えない場合も想定した<strong>二重の備え</strong>がポイントです。
                </Typography>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PhoneIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>連絡網の工夫</Typography>
                    </Box>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                            <ListItemText primary="LINEやSlackグループを活用し、災害時も素早く連絡" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                            <ListItemText primary="緊急時の連絡先（家族・管理会社・最寄りの避難所）を紙でも掲示" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                            <ListItemText primary="万が一のために、家ごとの「合言葉」や「集合場所」を決めておく" />
                        </ListItem>
                    </List>
                </Paper>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: '#f5f5f5', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <WarningIcon color="error" sx={{ mr: 1 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>停電や通信障害時の備え</Typography>
                    </Box>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><EmojiObjectsIcon color="warning" /></ListItemIcon>
                            <ListItemText primary="緊急用のホイッスルやライトを各部屋に" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><EmojiObjectsIcon color="warning" /></ListItemIcon>
                            <ListItemText primary="家の目立つ場所に「緊急連絡表」を掲示" />
                        </ListItem>
                    </List>
                </Paper>
                <Typography variant="body2" color="text.secondary">
                    日常から「もしもの時の連絡方法」を2人でシミュレーションしておくと、実際の災害時にも落ち着いて行動できます。
                </Typography>
            </Box>

            {/* 災害時の役割分担 */}
            <Box id="roles-in-disaster" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    災害時の役割分担
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアの強みは<b>「協力できる人数が多いこと」</b>。しかし、<b>役割が不明確</b>だと混乱のもとになります。<b>普段からの分担決め</b>と<b>柔軟な対応</b>が大切です。
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <DirectionsRunIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>主な役割例</Typography>
                            </Box>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><AssignmentIndIcon color="action" /></ListItemIcon>
                                    <ListItemText primary="避難誘導・点呼係" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><AssignmentIndIcon color="action" /></ListItemIcon>
                                    <ListItemText primary="備蓄品や救急箱の持ち出し担当" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><AssignmentIndIcon color="action" /></ListItemIcon>
                                    <ListItemText primary="外部との連絡・情報収集担当" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><AssignmentIndIcon color="action" /></ListItemIcon>
                                    <ListItemText primary="高齢者・体調不良者のサポート係" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: '#f5f5f5', height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <InfoIcon color="action" sx={{ mr: 1 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>分担ルールの工夫</Typography>
                            </Box>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="役割は「専任」ではなく「持ち回り」や「補助あり」で" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="体調や状況に応じて柔軟に変更OK" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="毎年見直し、メンバー交替時にも再確認" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Box sx={{ my: 2, textAlign: 'center' }}>
                    <img
                        src="/images/blog/teamwork-evacuation_roomshare-bousai-bouhan.jpg"
                        alt="避難訓練中、ルームメイトたちが互いに声を掛け合いながら、階段を安全に降りて避難するシーン。明るく落ち着いた雰囲気でチームワークの大切さが伝わる"
                        style={{
                            borderRadius: 12,
                            maxWidth: '100%',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.09)'
                        }}
                    />
                </Box>
                <Typography variant="body2" color="text.secondary">
                    「誰が何をするか」だけでなく、「みんなが協力できる空気づくり」も日常から意識しましょう。
                </Typography>
            </Box>

            {/* 専門家からのアドバイス */}
            <Box id="expert-tips" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    専門家からのアドバイス
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Paper elevation={2} sx={{ p: 3, borderRadius: 2, bgcolor: '#fafafa' }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        防災・防犯対策に「完璧」はありませんが、<b>「話し合うこと」</b>が何よりも大きな効果を生みます。ルームシェアでは、個人任せではなく、<b>みんなで小さなことから始める</b>のが長続きのコツです。
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><EmojiObjectsIcon color="primary" /></ListItemIcon>
                            <ListItemText primary="月に1度は、家の安全や備蓄を全員でチェック" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><EmojiObjectsIcon color="primary" /></ListItemIcon>
                            <ListItemText primary="新しい住人が入ったら、必ず防災・防犯ルールを一緒に確認" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><EmojiObjectsIcon color="primary" /></ListItemIcon>
                            <ListItemText primary="「緊急時の役割分担表」や「連絡表」は紙でも残す" />
                        </ListItem>
                    </List>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        防災・防犯の工夫は、家の雰囲気づくりや信頼関係にもつながります。小さな積み重ねが、大きな安心を生み出すのです。
                    </Typography>
                </Paper>
            </Box>

            {/* まとめ */}
            <Box id="summary" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: '#f5f5f5' }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        ルームシェアの防災・防犯対策は、「全員で作る安心感」が何よりの価値です。<b>共同でルールや備えを充実</b>させ、<b>日常の中で意識を高めていく</b>ことが、トラブルや災害時の混乱を最小限に抑えます。
                    </Typography>
                    <Grid container spacing={1} sx={{ mb: 2 }}>
                        <Grid item>
                            <Chip label="共同防災" color="primary" variant="outlined" />
                        </Grid>
                        <Grid item>
                            <Chip label="防犯強化" color="primary" variant="outlined" />
                        </Grid>
                        <Grid item>
                            <Chip label="緊急時連携" color="primary" variant="outlined" />
                        </Grid>
                        <Grid item>
                            <Chip label="役割分担" color="primary" variant="outlined" />
                        </Grid>
                    </Grid>
                    <Typography variant="body2" color="text.secondary">
                        小さな工夫の積み重ねが、あなたのルームシェア生活をより安全で、心豊かなものにしてくれるはずです。
                    </Typography>
                </Paper>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-bousai-bouhan',
    slug: 'roomshare-bousai-bouhan',
    title: 'ルームシェアの「防災・防犯」対策まとめ',
    excerpt: '共同防災対策\n防犯グッズ活用\n緊急連絡体制\n災害時の役割分担',
    coverImage: '/images/blog/cover-roomshare-bousai-bouhan_roomshare-bousai-bouhan.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。安心感と信頼関係が感じられる空間で、防災や防犯について話し合っている雰囲気。',
    publishedAt: '2024-06-16T00:00:00+09:00',
    isPublished: true,
    categories: ['ルームシェア', '防災', '防犯'],
    tags: ['ルームシェア', '防災対策', '防犯対策', '共同生活', '緊急連絡'],
    author: {
        name: '佐藤 真央',
        avatar: '/images/avatars/satou-mao.jpg',
        bio: 'ルームシェアアドバイザー。住宅管理や共同生活に関するコンサルティング、執筆歴10年以上。防災・防犯の啓発活動にも従事。',
        profilePicture: '/images/avatars/satou-mao.jpg'
    },
    viewCount: 1000,
    contentComponent: PostContent
};

export default post;